import { ListboxButton, ListboxOption } from "@headlessui/react"
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"

import { AttributeRef } from "./AddAttribute"
import { ListBox } from "../../lib/fragments/ListBox"
import { generateUniqueKey } from "../../utils/Helpers"
import { AddAttributeProps } from "../../utils/Interfaces"
import { FloatingInput } from "../../components/utils/FloatingInput"
import { FloatingTextArea } from "../../components/utils/FloatingTextArea"
import { G_onInputChangeHandler, G_onInputBlurHandler } from "../../components/utils/InputHandlers"
import { ToggleSwitch } from "../../components/utils/ToggleSwitch"

export const ChoiceAttribute = forwardRef<AttributeRef, AddAttributeProps>(({ whatToAdd, posting }, ref) => {
    type ErrorOption = {
        name: string;
        hint?: string;
    };

    type ErrorOptionArray = {
        errOptions: ErrorOption[];
    };

    // Define the complete state shape
    type AppState = {
        errors: ErrorOptionArray;
    };

    const [state, setstate] = useState({
        httpStatus: 200,
        input: {
            name: '',
            subtitle: '',
            mandatory: false,
            display: {
                type: "Radio Buttons",
            },
            options: [
                { label: 'Option 1' },
                { label: 'Option 2' },
            ]
        },
        errors: {
            name: '',
            subtitle: '',
            display: '',
            errOptions: [
                { name: '' },
                { name: '' },
            ]
        }
    })

    const displayBoxId = generateUniqueKey(17)
    const [otherExists, setOtherExists] = useState(false)
    const [displayBoxWidth, setRestictionsBoxWidth] = useState(0)
    const [subtitleVisibility, setSubtitleVisibility] = useState(false)

    const displayList = [
        { type: "Dropdown" },
        { type: "Checkboxes" },
        { type: "Radio Buttons" },
    ]

    useEffect(() => {
        setRestictionsBoxWidth(document.getElementById(displayBoxId).offsetWidth)
    }, [
        displayBoxId,
        displayBoxWidth,
        setRestictionsBoxWidth
    ]);

    const onChangeHandler = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputChangeHandler(e)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const targetLength = (name: string) => {
        switch (name) {
            case 'subtitle':
                return {
                    min: 5,
                    max: 100
                };

            default:
                return {
                    min: 3,
                    max: 30
                };
        }
    }

    const onInputBlur = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state

            const target0 = targetLength(e.target.name)
            let output: any = G_onInputBlurHandler(e, '', target0.min, target0.max)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const removeSubtitle = () => {
        setSubtitleVisibility(false)

        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                subtitle: ''
            }, errors: {
                ...prev.errors,
                subtitle: ''
            }
        }))
    }

    const onListBoxHandler = (e: any, label: any) => {
        let { input } = state
        let updatedInput: any = {}

        if (e.name === 'Numerical' || e.name === 'URL') {
            updatedInput = {
                ...input,
                [label]: e,
                multiline: false,
            }
        } else {
            updatedInput = {
                ...input,
                [label]: e
            }
        }

        setstate((prev) => ({
            ...prev,
            input: updatedInput,
        }));
    }

    const handleCheckboxChange = (e: any) => {
        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                [e.target.name]: e.target.checked
            }
        }))
    }

    const addChoiceOption = () => {
        if (!posting) {
            setstate(prevState => {
                let options = [...prevState.input.options];
                let errOptions = [...prevState.errors.errOptions];

                const otherOptionExists = options.some(option => option.label === 'Other [SYS]');

                if (otherOptionExists) {
                    // If "Other" exists, add the new option before it
                    const otherIndex = options.findIndex(option => option.label === 'Other [SYS]');
                    options.splice(otherIndex, 0, { label: `Option ${options.length}` });

                    // Ensure "Other [SYS]" is always the last option if it exists
                    if (otherIndex === -1 && !options.some(option => option.label === 'Other [SYS]')) {
                        options.push({ label: 'Other [SYS]' });
                    }
                } else {
                    // If "Other" doesn't exist, add the new option at the end
                    options.push({ label: `Option ${options.length + 1}` });
                }

                // Add a respective errors option
                errOptions.push({ name: `` });

                return {
                    ...prevState,
                    input: {
                        ...prevState.input,
                        options
                    },
                    errors: {
                        ...prevState.errors,
                        errOptions
                    }
                };
            })
        }
    }

    const addOtherChoiceOption = () => {
        if (!posting) {
            setOtherExists(true)

            setstate(prevState => ({
                ...prevState,
                input: {
                    ...prevState.input,
                    options: [
                        ...prevState.input.options,
                        { label: 'Other [SYS]' } // Always add "Other" as the last option
                    ]
                }
            }));
        }
    };

    const removeChoiceOption = (index: any) => {
        if (!posting) {
            setstate(prevState => {
                let { input } = state
                const optionToRemove = input.options[index]?.label;

                if (optionToRemove === 'Other [SYS]') {
                    setOtherExists(false)
                }

                let options = [...prevState.input.options];
                let errOptions = [...prevState.errors.errOptions];

                // Remove the option at the specified index
                options.splice(index, 1);
                errOptions.splice(index, 1);

                // Ensure no duplicate labels
                options = options.filter((value, index, self) =>
                    index === self.findIndex((t) => (
                        t.label === value.label
                    ))
                );

                if (options.length === 0) {
                    // If no options are available, add one default
                    options.push({ label: `Option ${options.length + 1}` });

                    // Add a respective errors option
                    errOptions.push({ name: `` });
                }

                return {
                    ...prevState,
                    input: {
                        ...prevState.input,
                        options
                    },
                    errors: {
                        ...prevState.errors,
                        errOptions
                    }
                };
            });
        }
    };

    const optionChangeHandler = (e: any, index: number) => {
        if (!posting) {
            const { input, errors } = state;
            const output = G_onInputChangeHandler(e);

            const newChoiceOptions = input.options.map((option: any, mapIndex: number) => {
                if (index !== mapIndex) return option; // Return unchanged options for other indices

                // Update the error and option for the specific index
                const updatedOption = { ...option, label: output.value || e.target.value };
                errors.errOptions[index].name = output.error || ''; // Set error if present
                return updatedOption;
            });

            setstate((prevState: any) => ({
                ...prevState,
                input: {
                    ...prevState.input,
                    options: newChoiceOptions,
                },
                errors: {
                    ...prevState.errors,
                },
            }));
        }
    };

    const optionBlurHandler = (e: any, index: any) => {
        if (!posting) {
            const { input } = state;
            const { errors } = state;
            const currentName = e.target.value;

            // Check if the label is a duplicate
            const isDuplicate = input.options.some(
                (option, i) => i !== index && option.label === currentName
            );

            if (isDuplicate) {
                // Update the specific error in the errOptions array
                const updatedErrorOptions = [...errors.errOptions]

                updatedErrorOptions[index] = {
                    ...updatedErrorOptions[index],
                    name: "Duplicate detected: Each option should be unique",
                }

                // Update state with the modified errors object
                setstate(prevState => ({
                    ...prevState,
                    errors: {
                        ...prevState.errors,
                        errOptions: updatedErrorOptions, // Assign to errOptions directly
                    },
                }));
            } else {
                // Update the original label attribute if no duplicate
                e.target.setAttribute("data-original-label", currentName);
            }
        }
    };

    const addOptionHint = (index: number) => {
        if (!posting) {
            setstate((prevState) => {
                const newOptions: any = [...prevState.input.options];
                newOptions[index] = { ...newOptions[index], hint: "" };

                return {
                    ...prevState,
                    input: {
                        ...prevState.input,
                        options: newOptions
                    }
                };
            });
        }
    };

    const removeOptionHint = (index: number) => {
        if (!posting) {
            setstate((prevState) => {
                const newOptions: any = [...prevState.input.options];
                delete newOptions[index].hint;

                return {
                    ...prevState,
                    input:
                    {
                        ...prevState.input,
                        options: newOptions
                    }
                };
            });
        }
    };

    const optionHintChangeHandler = (e: any, index: number) => {
        if (!posting) {
            const { input, errors }: any = state;
            const output = G_onInputChangeHandler(e);

            const newChoiceOptions = input.options.map((option: any, mapIndex: number) => {
                if (index !== mapIndex) return option;

                // Update the error and option for the specific index
                const updatedOption = {
                    ...option,
                    hint: output.value || e.target.value
                }

                errors.errOptions[index].hint = output.error || '';
                return updatedOption;
            });

            setstate((prevState: any) => ({
                ...prevState,
                input: {
                    ...prevState.input,
                    options: newChoiceOptions,
                },
                errors: {
                    ...prevState.errors,
                },
            }));
        }
    }

    const optionHintBlurHandler = (e: any, index: any) => {
        if (!posting) {
            const { errors } = state;

            const output = G_onInputBlurHandler(e, '')
            const updatedErrorOptions: any = [...errors.errOptions]

            updatedErrorOptions[index] = {
                ...updatedErrorOptions[index],
                hint: output.error,
            }

            setstate(prevState => ({
                ...prevState,
                errors: {
                    ...prevState.errors,
                    errOptions: updatedErrorOptions, // Assign to errOptions directly
                },
            }));
        }
    };

    useImperativeHandle(ref, () => ({
        integrityCheck: (e: any) => {
            let { input } = state
            let { errors } = state
            const inputArray = Object.keys(input)

            let passedValidation = true
            let attributes = input

            errors.errOptions.forEach((error: any) => {
                if (error.name.length > 0) {
                    return
                }
            })

            inputArray.forEach((inputObject: any) => {
                let oTarget = e.target[inputObject]
                let event: any = {
                    target: {
                        name: inputObject,
                    }
                }

                switch (inputObject) {
                    case 'subtitle':
                        if (subtitleVisibility) {
                            event.target.value = oTarget.value.trim()
                            event.target.required = oTarget.required

                            onInputBlur(event)
                        }
                        break

                    case 'options':
                        // Explicitly do nothing
                        break

                    case 'mandatory':
                    case 'multiline':
                    case 'display':
                        event.target.value = input[inputObject]
                        event.target.required = false
                        break

                    default:
                        event.target.value = oTarget.value.trim()
                        event.target.required = oTarget.required

                        onInputBlur(event)
                        break;
                }
            })

            errors = state.errors
            const errorArray = Object.keys(errors)

            errorArray.forEach((errorObject) => {
                if (errorObject !== 'errOptions' && errors[errorObject]?.length > 0) {
                    passedValidation = false;
                }
            })

            return {
                passedValidation,
                attributes
            }
        },
    }));

    return (
        <React.Fragment>
            <div className="w-full mb-3">
                <p className="text-lg text-slate-900 dark:text-white flex items-center font-medium gap-x-3 py-2">
                    <span className={`fa-light ${whatToAdd.icon} fa-lg`}></span>
                    {whatToAdd.name}
                </p>

                <p className="text-sm pb-1 pt-1">
                    Create pre-defined dropdowns, checkboxes, or radio buttons
                </p>
            </div>

            <div className="w-full flex md:flex-row flex-col items-baseline md:gap-x-2">
                <div className="md:w-3/4 w-full">
                    <FloatingInput
                        name={"name"}
                        label={"Choice to be captured"}
                        required={true}
                        posting={posting}
                        value={state.input.name}
                        error={state.errors.name}
                        blurHandler={onInputBlur}
                        changeHandler={onChangeHandler}
                    />
                </div>

                <div className="flex-grow md:flex justify-center w-full md:w-auto">
                    <button type="button" onClick={() => setSubtitleVisibility(true)} className={`text-theme pt-3 md:py-0 hover:text-orange-700 ${subtitleVisibility ? 'hidden' : 'block md:mb-0 mb-3'}`}>
                        <span className="text-sm">Add Subtitle</span>
                    </button>
                </div>
            </div>

            {
                subtitleVisibility ? (
                    <>
                        <div className="mt-3 md:py-0 md:pt-2 py-3 w-full bg-sky-00">
                            <FloatingTextArea
                                name={"subtitle"}
                                label={"Why is this data needed?"}
                                required={false}
                                posting={posting}
                                value={state.input.subtitle}
                                error={state.errors.subtitle}
                                blurHandler={onInputBlur}
                                changeHandler={onChangeHandler}
                            />

                            <div className="w-full md:w-auto flex flex-row-reverse">
                                <button type="button" onClick={() => removeSubtitle()} className={`text-theme md:py-0 hover:text-orange-700`}>
                                    <span className="text-sm">Remove Subtitle</span>
                                </button>
                            </div>
                        </div>
                    </>
                ) : null
            }

            <div className="w-full md:pt-3 pb-1">
                <div className="w-full pb-3">
                    <p className='label flex items-center text-slate-700 dark:text-slate-300 text-base'>
                        Display Style
                    </p>

                    <p className='label items-center text-slate-500 dark:text-slate-400 text-sm'>
                        Decide how the options will be displayed to users
                    </p>
                </div>

                <div className="flex-none md:w-auto w-full pb-4">
                    <ListBox
                        width={displayBoxWidth}
                        value={state.input.display}
                        eventHandler={(e: any,) => onListBoxHandler(e, 'display')}
                        button={
                            <>
                                <ListboxButton id={displayBoxId} className="relative border md:w-52 w-full rounded-md py-2 pr-4 text-left cursor-default focus:ring-primary focus:border-primary sm:text-sm ring-slate-300 dark:ring-slate-600 focus:ring-orange-600 focus:outline-none focus:border-0 focus:ring-primary focus:outline-orange-500 border-slate-400 dark:border-slate-600 dark:text-white">
                                    <span className="flex w-full items-center align-middle">
                                        <div className="flex-grow flex items-center">
                                            <span className="truncate ml-3 font-normal group-data-[selected]:font-semibold">
                                                {
                                                    state.input.display ? (
                                                        <div className="flex items-center gap-x-4">
                                                            <span className="truncate group-data-[selected]:font-semibold">
                                                                {state.input.display.type}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            -- Set restriction
                                                        </>
                                                    )
                                                }
                                            </span>
                                        </div>

                                        <span className="fa-light fa-chevron-down focus:text-primary float-end"></span>
                                    </span>
                                </ListboxButton>
                            </>
                        }
                        options={
                            <>
                                {
                                    displayList.map((display: any) => (
                                        <ListboxOption key={display.type} value={display} className="group relative cursor-default select-none py-2 pl-3 pr-9 data-[focus]:bg-[var(--focus-bg-light)] data-[focus]:text-[var(--color-secondary)] rounded-md">
                                            <div className="flex items-center gap-x-4">
                                                <span className="truncate group-data-[selected]:font-semibold">
                                                    {display.type}
                                                </span>
                                            </div>

                                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                                                <span className="fa-light fa-check"></span>
                                            </span>
                                        </ListboxOption>
                                    ))
                                }
                            </>
                        }
                    />
                </div>
            </div>

            <div className="w-full pb-1 pt-1 rounded-md">
                <div className="w-full pb-3">
                    <p className='label flex items-center text-slate-700 dark:text-slate-300 text-base'>
                        Options
                    </p>

                    <p className='label items-center text-slate-500 dark:text-slate-400 text-sm'>
                        Specify the set of selectable options
                    </p>
                </div>

                <ul className="space-y-2">
                    {
                        state.input.options.map((option: any, index: number) => {
                            console.log('OPion', state);

                            return (
                                <li key={`option-lst-${index}`} className="w-full flex flex-col align-middle items-start gap-y-2 border-b py-3 first:pt-0">
                                    <div className="w-full flex gap-x-2">
                                        {
                                            state.input.display.type === 'Checkboxes' ? (
                                                <span className="fill-inherit stroke-slate-400 w-5 h-5 pt-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                        <rect width="19" height="19" x="2.5" y="2.5" fill="#fff" rx="1.5"></rect>
                                                    </svg>
                                                </span>
                                            ) : state.input.display.type === 'Radio Buttons' ? (
                                                <span className="fill-inherit stroke-slate-400 w-6 h-6 pt-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                        <path fill="#fff" d="M12 21.5a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19Z"></path>
                                                    </svg>
                                                </span>
                                            ) : null
                                        }

                                        <div className="flex-grow">
                                            <input
                                                type="text"
                                                name={`option-itm-${index}`}
                                                id={`option-itm-${index}`}
                                                autoComplete="off"
                                                className="focus:ring-1 w-full focus:ring-green-500 px-3 py-1 block text-sm rounded-md border border-gray-300 disabled:opacity-80"
                                                onChange={(e) => optionChangeHandler(e, index)}
                                                onBlur={(e) => optionBlurHandler(e, index)}
                                                value={option.label.replace(" [SYS]", "")}
                                                disabled={option.label === "Other [SYS]" ? true : false}
                                            />

                                            {
                                                option.hint !== undefined ? (
                                                    <div className="pt-2">
                                                        <input
                                                            type="text"
                                                            name="hint"
                                                            value={option.hint}
                                                            placeholder="Hint (optional)"
                                                            onBlur={(e) => optionHintBlurHandler(e, index)}
                                                            onChange={(e) => optionHintChangeHandler(e, index)}
                                                            className="focus:ring-1 w-full focus:ring-blue-500 px-3 py-1 block text-xs leading-5 rounded-md border border-gray-300"
                                                        />
                                                    </div>
                                                ) : null
                                            }
                                        </div>
                                    </div>

                                    {(() => {
                                        let { errors }: any = state

                                        return (
                                            <>
                                                {
                                                    errors.errOptions[index] && (
                                                        errors.errOptions[index]?.name?.length > 0 ? (
                                                            <div className="w-full md:pl-7 py-1">
                                                                <p className="invalid-feedback px-2 text-xs pb-1 bg-red-50 text-red-600 pt-0.5">
                                                                    {errors.errOptions[index].name}
                                                                </p>
                                                            </div>
                                                        ) : errors.errOptions[index]?.hint?.length > 0 ? (
                                                            <div className="w-full md:pl-7 py-1">
                                                                <p className="invalid-feedback px-2 text-xs pb-1 bg-red-50 text-red-600 pt-0.5">
                                                                    {errors.errOptions[index].hint}
                                                                </p>
                                                            </div>
                                                        ) : null
                                                    )
                                                }
                                            </>
                                        )
                                    })()}

                                    <div className="w-full flex flex-row gap-x-2 items-center align-middle md:pl-7">
                                        <div className="flex-1">
                                            {
                                                option.hint !== undefined ? (
                                                    <>
                                                        <button type="button" onClick={() => removeOptionHint(index)} className="text-gray-500 underline px-2 md:py-0 hover:text-orange-700 text-sm">
                                                            Remove Hint
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button type="button" onClick={() => addOptionHint(index)} className="text-blue-500 px-2 md:py-0 hover:text-orange-700 text-sm">
                                                        Add Hint
                                                    </button>
                                                )
                                            }
                                        </div>

                                        <div className="flex-1">
                                            <button type="button" onClick={() => removeChoiceOption(index)} className="text-red-500 float-right px-2 md:py-0 hover:text-orange-700 text-sm">
                                                {/* <span className="fa-light fa-trash fa-lg"></span> */}
                                                Delete Option
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })
                    }
                </ul>

                <div className="w-full flex pt-4 pb-2 align-middle gap-x-2">
                    <button type="button" onClick={() => addChoiceOption()} className={`text-theme md:py-0 hover:text-orange-700`}>
                        <span className="text-sm">Add Option</span>
                    </button>

                    {
                        !otherExists ? (
                            <>
                                <span className="border-l border-slate-300 dark:border-slate-600  mx-3"></span>

                                <button type="button" onClick={() => addOtherChoiceOption()} className={`text-theme md:py-0 hover:text-orange-700`}>
                                    <span className="text-sm">Add "Other" Option</span>
                                </button>
                            </>
                        ) : null
                    }
                </div>
            </div>

            <div className="w-full py-4">
                <ToggleSwitch
                    label={'Mandatory'}
                    name={'mandatory'}
                    disabled={false}
                    changeHandler={handleCheckboxChange}
                    checked={state.input.mandatory}
                    description={"Ensures users provide a value before submitting."}
                />
            </div>
        </React.Fragment >
    )
})